import React, { useState, useEffect } from 'react'
import { API_ENDPOINTS } from '../../../config/api'
import apiClient from '../../../config/api'

const brandColors = {
  primary: '#1338b5',
  mid: '#1f4dc7',
  bright: '#5c8ef0',
}

const LaybyList = ({ userInfo, onViewDetail }) => {
  const [laybys, setLaybys] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filterStatus, setFilterStatus] = useState('all') // 'all', 'active', 'completed', 'overdue'

  useEffect(() => {
    fetchLaybys()
  }, [])

  const fetchLaybys = async () => {
    setLoading(true)
    setError('')

    try {
      const response = await apiClient.get(API_ENDPOINTS.LAYBY)
      setLaybys(Array.isArray(response.data) ? response.data : [])
    } catch (err) {
      console.error('Error fetching laybys:', err)
      setError(
        err.response?.data?.detail ||
          err.response?.data?.message ||
          'Failed to load laybys'
      )
    } finally {
      setLoading(false)
    }
  }

  const filteredLaybys = laybys.filter((layby) => {
    if (filterStatus === 'all') return true
    if (filterStatus === 'active') return layby.status !== 'completed'
    if (filterStatus === 'completed') return layby.status === 'completed'
    if (filterStatus === 'overdue') {
      const dueDate = new Date(layby.due_date)
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      return layby.status !== 'completed' && dueDate < today
    }
    return true
  })

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { bg: 'bg-primary', text: 'Active' },
      completed: { bg: 'bg-success', text: 'Completed' },
      cancelled: { bg: 'bg-danger', text: 'Cancelled' },
      default: { bg: 'bg-secondary', text: status || 'Unknown' },
    }

    const config = statusConfig[status?.toLowerCase()] || statusConfig.default

    return (
      <span className={`badge ${config.bg} text-white`}>{config.text}</span>
    )
  }

  const formatCurrency = (value) => {
    return `$${Number(value).toFixed(2)}`
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const isOverdue = (dueDate, status) => {
    if (status === 'completed') return false
    const due = new Date(dueDate)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return due < today
  }

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading laybys...</span>
        </div>
        <p className="mt-3 text-muted">Loading laybys...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        <i className="bi bi-exclamation-triangle me-2" />
        {error}
        <button
          className="btn btn-sm btn-outline-danger ms-3"
          onClick={fetchLaybys}
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div>
      {/* Filter Controls */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <div className="d-flex gap-2 flex-wrap">
          <button
            className={`btn btn-sm ${filterStatus === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setFilterStatus('all')}
          >
            All ({laybys.length})
          </button>
          <button
            className={`btn btn-sm ${filterStatus === 'active' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setFilterStatus('active')}
          >
            Active ({laybys.filter((l) => l.status !== 'completed').length})
          </button>
          <button
            className={`btn btn-sm ${filterStatus === 'completed' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setFilterStatus('completed')}
          >
            Completed ({laybys.filter((l) => l.status === 'completed').length})
          </button>
          <button
            className={`btn btn-sm ${filterStatus === 'overdue' ? 'btn-danger' : 'btn-outline-danger'}`}
            onClick={() => setFilterStatus('overdue')}
          >
            Overdue (
            {
              laybys.filter((l) =>
                isOverdue(l.due_date, l.status)
              ).length
            }
            )
          </button>
        </div>
        <button
          className="btn btn-sm btn-outline-secondary"
          onClick={fetchLaybys}
          title="Refresh"
        >
          <i className="bi bi-arrow-clockwise me-1" />
          Refresh
        </button>
      </div>

      {/* Laybys List */}
      {filteredLaybys.length === 0 ? (
        <div className="alert alert-light border text-center py-5">
          <i className="bi bi-inbox fs-1 text-muted mb-3 d-block" />
          <p className="text-muted mb-0">
            No laybys found
            {filterStatus !== 'all' && ` matching "${filterStatus}" status`}
          </p>
        </div>
      ) : (
        <div className="list-group">
          {filteredLaybys.map((layby) => (
            <div
              key={layby.id}
              className="list-group-item list-group-item-action"
              style={{ cursor: 'pointer' }}
              onClick={() => onViewDetail(layby)}
            >
              <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                <div className="flex-grow-1">
                  <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                    <h6 className="mb-0 fw-bold">{layby.layby_number}</h6>
                    {getStatusBadge(layby.status)}
                    {isOverdue(layby.due_date, layby.status) && (
                      <span className="badge bg-danger">Overdue</span>
                    )}
                  </div>

                  <div className="mb-2">
                    <p className="mb-1">
                      <i className="bi bi-person me-2 text-muted" />
                      <strong>{layby.customer_name}</strong>
                    </p>
                    <p className="mb-1 text-muted small">
                      <i className="bi bi-telephone me-2" />
                      {layby.customer_phone}
                    </p>
                    <p className="mb-1 text-muted small">
                      <i className="bi bi-calendar me-2" />
                      Due: {formatDate(layby.due_date)}
                      {isOverdue(layby.due_date, layby.status) && (
                        <span className="text-danger ms-2">
                          (Overdue)
                        </span>
                      )}
                    </p>
                    <p className="mb-0 text-muted small">
                      <i className="bi bi-shop me-2" />
                      {layby.branch_name || 'N/A'}
                    </p>
                  </div>

                  <div className="d-flex gap-3 flex-wrap small">
                    <span>
                      <strong>Total:</strong> {formatCurrency(layby.total_amount)}
                    </span>
                    <span>
                      <strong>Deposit:</strong> {formatCurrency(layby.deposit)}
                    </span>
                    <span className={Number(layby.balance) > 0 ? 'text-warning' : 'text-success'}>
                      <strong>Balance:</strong> {formatCurrency(layby.balance)}
                    </span>
                    <span>
                      <strong>Paid:</strong> {formatCurrency(layby.total_paid)}
                    </span>
                  </div>
                </div>

                <div className="text-end">
                  <div className="mb-2">
                    <span className="badge bg-light text-dark border">
                      {layby.items?.length || 0} item{layby.items?.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <button
                    className="btn btn-sm"
                    style={{
                      background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.mid} 100%)`,
                      color: '#fff',
                      border: 'none',
                    }}
                    onClick={(e) => {
                      e.stopPropagation()
                      onViewDetail(layby)
                    }}
                  >
                    <i className="bi bi-eye me-1" />
                    View Details
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default LaybyList
